import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useWallet } from '../hooks/useWallet';
import { ethers } from 'ethers';
import { FaBug, FaChevronDown, FaChevronUp, FaCheckCircle, FaTimesCircle, FaQuestionCircle, FaCopy, FaSync, FaEye, FaEyeSlash, FaTrash } from 'react-icons/fa';
import { removeAgentPrivateKey } from '../utils/privateKey';
import { useInstanceStore } from '../stores/UseInstanceStore';
import { portfolioFetch } from '../utils/portfolioFetch';

export const HLAgentDebugPanel = ({
  config = { apiBaseUrl: "http://localhost:8000" },
  authToken,
  portfolioId,
  portfolio,
  onBuilderFeeApproved,
}) => {

  const [showDebug, setShowDebug] = useState(true);
  const [realAgentApproved, setRealAgentApproved] = useState(null);
  const [checkingApproval, setCheckingApproval] = useState(false);
  const [builderFeeApproving, setBuilderFeeApproving] = useState(false);
  const [builderFeeStatus, setBuilderFeeStatus] = useState(null); // null | 'success' | 'error'
  const [builderFeeMessage, setBuilderFeeMessage] = useState('');
  const [builderFeeApproved, setBuilderFeeApproved] = useState(null);
  const [checkingBuilderFee, setCheckingBuilderFee] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [copiedAgentKey, setCopiedAgentKey] = useState(false);
  const [showImportKey, setShowImportKey] = useState(false);
  const [importKeyInput, setImportKeyInput] = useState('');
  const [importKeyError, setImportKeyError] = useState('');
  const [importKeySuccess, setImportKeySuccess] = useState(false);

  const { jwtToken, isTestnetNetwork, network, account, signer } = useWallet();
  const bumpAgentKeyVersion = useInstanceStore((state) => state.bumpAgentKeyVersion);
  const agentKeyVersion = useInstanceStore((state) => state.instance.agentKeyVersion || 0);

  const effectiveAuthToken = authToken || jwtToken;
  const authHeaders = useMemo(() => (
    effectiveAuthToken
      ? {
          accept: 'application/json',
          Authorization: `Bearer ${effectiveAuthToken}`,
        }
      : null
  ), [effectiveAuthToken]);

  const stableConfig = useMemo(() => config, [config.apiBaseUrl]);

  const agentPayload = useMemo(() => {
    if (!portfolio || !portfolioId) return null;
    if (portfolio.portfolio_type !== 'HYPERLIQUID') return null;

    const storageKey = `hyperliquid_agent_${portfolio.is_testnet ? 'testnet' : 'mainnet'}_${portfolioId}`;
    const agentPrivateKey = localStorage.getItem(storageKey);

    if (!agentPrivateKey) {
      console.log('No agent private key found in localStorage for portfolio:', portfolioId);
      return null;
    }

    let agentAddress = null;
    try {
      const wallet = new ethers.Wallet(agentPrivateKey);
      agentAddress = wallet.address;
    } catch (error) {
      console.error('Failed to derive agent address from private key:', error);
      agentAddress = 'Invalid private key';
    }

    return {
      agent_private_key: agentPrivateKey,
      agent_address: agentAddress,
      user_master_address: account,
      is_testnet: portfolio.is_testnet
    };
  }, [portfolio, portfolioId, account, agentKeyVersion]);


  const handleImportAgentKey = () => {
    setImportKeyError('');
    setImportKeySuccess(false);
    const raw = importKeyInput.trim();
    if (!raw) {
      setImportKeyError('Please enter a private key.');
      return;
    }
    try {
      const wallet = new ethers.Wallet(raw);
      const storageKey = `hyperliquid_agent_${portfolio.is_testnet ? 'testnet' : 'mainnet'}_${portfolioId}`;
      localStorage.setItem(storageKey, raw);
      bumpAgentKeyVersion();
      setImportKeySuccess(true);
      setImportKeyInput('');
      setTimeout(() => {
        setShowImportKey(false);
        setImportKeySuccess(false);
      }, 1200);
    } catch (e) {
      setImportKeyError('Invalid private key — could not derive address. Check and try again.');
    }
  };

  const handleApproveBuilderFee = async () => {
    if (!signer || !agentPayload || !effectiveAuthToken) return;

    const builderAddress = config?.builderAddress || '0xDdf3A93522D503a43f802BeB4882BfF18D940a20';
    const builderFeeRate = config?.builderFeeRate || '0.0100%';
    const isMainnet = !agentPayload.is_testnet;

    try {
      setBuilderFeeApproving(true);
      setBuilderFeeStatus(null);
      setBuilderFeeMessage('');

      const nonce = Date.now();
      // Use wallet's chain (user's connected chain), not Hyperliquid's chain
      // Hyperliquid testnet/mainnet is separate from blockchain network selection
      const walletChainId = network?.chainId ?? (isMainnet ? 42161 : 421614);
      const signatureChainId = '0x' + walletChainId.toString(16);

      const domain = {
        name: 'HyperliquidSignTransaction',
        version: '1',
        chainId: walletChainId,
        verifyingContract: '0x0000000000000000000000000000000000000000',
      };

      const types = {
        'HyperliquidTransaction:ApproveBuilderFee': [
          { name: 'hyperliquidChain', type: 'string' },
          { name: 'maxFeeRate',       type: 'string' },
          { name: 'builder',          type: 'address' },
          { name: 'nonce',            type: 'uint64'  },
        ],
      };

      const action = {
        type:             'approveBuilderFee',
        hyperliquidChain: isMainnet ? 'Mainnet' : 'Testnet',
        signatureChainId,
        maxFeeRate:       builderFeeRate,
        builder:          builderAddress,
        nonce,
      };

      const rawSig = await signer._signTypedData(domain, types, {
        hyperliquidChain: action.hyperliquidChain,
        maxFeeRate:       action.maxFeeRate,
        builder:          action.builder,
        nonce:            action.nonce,
      });

      const r = rawSig.slice(0, 66);
      const s = '0x' + rawSig.slice(66, 130);
      const v = parseInt(rawSig.slice(130, 132), 16);
      const signature = { r, s, v };

      const approveUrl = `${stableConfig.apiBaseUrl}/portfolio/${portfolioId}/approve-builder-fee`;
      const resp = await fetch(approveUrl, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action, signature, nonce }),
      });

      if (!resp.ok) {
        const errText = await resp.text();
        throw new Error(`${resp.status} ${errText}`);
      }

      setBuilderFeeStatus('success');
      setBuilderFeeMessage(`Builder fee (${builderFeeRate}) approved successfully.`);
      setBuilderFeeApproved(true);
      if (onBuilderFeeApproved) {
        setTimeout(() => onBuilderFeeApproved(), 500);
      }
    } catch (err) {
      console.error('Builder fee approval error:', err);
      setBuilderFeeStatus('error');
      setBuilderFeeMessage(`Approval failed: ${err.message}`);
    } finally {
      setBuilderFeeApproving(false);
    }
  };

  const checkBuilderFeeApproval = useCallback(async () => {
    if (!portfolio) {
      setBuilderFeeApproved(null);
      return;
    }

    setCheckingBuilderFee(true);
    setBuilderFeeApproved(portfolio.builder_fee_approved === true);
    setCheckingBuilderFee(false);
  }, [portfolio]);

  const checkRealAgentApproval = useCallback(async () => {
    if (!effectiveAuthToken || !authHeaders) {
      setRealAgentApproved(null);
      return;
    }

    if (!agentPayload?.agent_address || !agentPayload?.user_master_address) {
      console.log('No agent payload available for approval check');
      return;
    }

    setCheckingApproval(true);
    try {
      const getResponse = await portfolioFetch(`${stableConfig.apiBaseUrl}/portfolio/${portfolioId}/agent-approval-status/${agentPayload.agent_address}`, {
        headers: authHeaders
      }, portfolioId);

      if (!getResponse.ok) {
        throw new Error(`HTTP ${getResponse.status}: ${getResponse.statusText}`);
      }

      const getResult = await getResponse.json();

      if (agentPayload.agent_private_key) {
        const postResponse = await portfolioFetch(`${stableConfig.apiBaseUrl}/portfolio/${portfolioId}/agent-approval-status/${agentPayload.agent_address}`, {
          method: 'POST',
          headers: {
            ...authHeaders,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            agent_private_key: agentPayload.agent_private_key,
            user_master_address: agentPayload.user_master_address
          })
        }, portfolioId);

        if (postResponse.ok) {
          const postResult = await postResponse.json();
          setRealAgentApproved(postResult.approved);
        } else {
          setRealAgentApproved(getResult.approved);
        }
      } else {
        setRealAgentApproved(getResult.approved);
      }

    } catch (error) {
      console.error('Failed to check agent approval:', error);
      setRealAgentApproved(null);
    } finally {
      setCheckingApproval(false);
    }
  }, [agentPayload, stableConfig.apiBaseUrl, effectiveAuthToken, authHeaders, portfolioId]);

  const handleRefresh = useCallback(() => {
    checkBuilderFeeApproval();
    if (effectiveAuthToken) {
      checkRealAgentApproval();
    } else {
      setRealAgentApproved(null);
    }
  }, [effectiveAuthToken, checkRealAgentApproval, checkBuilderFeeApproval]);

  // Run once on mount only — all subsequent refreshes are manual via the Refresh button
  useEffect(() => {
    handleRefresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopyAddress = () => {
    if (!agentPayload?.agent_address) return;
    navigator.clipboard.writeText(agentPayload.agent_address);
    setCopiedAddress(true);
    setTimeout(() => setCopiedAddress(false), 2000);
  };

  if (!portfolio || portfolio.portfolio_type !== 'HYPERLIQUID') {
    return null;
  }

  const isTestnet = portfolio?.is_testnet;

  return (
    <div className="w-full">
      {/* Toggle Button */}
      {/* <button
        onClick={() => setShowDebug(!showDebug)}
        className="flex items-center gap-1.5 px-2 py-1 rounded bg-slate-700/50 hover:bg-slate-700 border border-slate-600 text-slate-300 hover:text-white text-xs transition-colors duration-150"
      >
        <FaBug className="text-purple-400" size={10} />
        <span>Agent Debug</span>
        {showDebug ? <FaChevronUp size={9} className="text-slate-400" /> : <FaChevronDown size={9} className="text-slate-400" />}
      </button> */}

      {/* Debug Panel */}
      {showDebug && (
        <div className="mt-1 rounded-lg border border-slate-600 bg-gray-800/95 overflow-hidden text-xs">
          {/* Panel title */}
          <div className="flex items-center gap-2 px-3 py-2.5 bg-slate-700/50 border-b border-slate-600">
            <FaBug className="text-purple-400" size={12} />
            <span className="font-semibold text-slate-200 text-sm">Hyperliquid Debug</span>
          </div>
          {/* Info rows */}
          <h3>Agent Debug Info</h3>
          <div className="px-3 py-2 space-y-0.5 border-b border-slate-700">
            <DRow label="Portfolio" value={portfolioId || '—'} mono />
            <DRow label="Network" value={network ? `${network.name} (${network.chainId})` : '—'} />
            <DRow
              label="Web3 Chain"
              value={isTestnetNetwork() ? 'Arbitrum Sepolia' : 'Arbitrum Mainnet'}
              valueClass={isTestnetNetwork() ? 'text-yellow-400' : 'text-emerald-400'}
            />
            <DRow
              label="HL Chain"
              value={isTestnet ? 'HL Testnet' : 'HL Mainnet'}
              valueClass={isTestnet ? 'text-yellow-400' : 'text-emerald-400'}
            />
            <DRow label="Auth" value={effectiveAuthToken ? 'Available' : 'Missing'} valueClass={effectiveAuthToken ? 'text-emerald-400' : 'text-red-400'} />
            <DRow label="Master" value={account || '—'} mono valueClass="text-blue-400" />
            <DRow label="Agent" value={agentPayload?.agent_address || '—'} mono valueClass={agentPayload?.agent_address ? 'text-emerald-400' : 'text-slate-500'} />
            {/* Import agent key — shown when no local key is found */}
            {!agentPayload?.agent_private_key && (
              <div className="pt-1">
                {!showImportKey ? (
                  <button
                    onClick={() => { setShowImportKey(true); setImportKeyError(''); setImportKeyInput(''); }}
                    className="flex items-center gap-1 px-2 py-0.5 bg-yellow-700/60 hover:bg-yellow-700 border border-yellow-600 text-yellow-200 rounded text-xs transition-colors"
                  >
                    <FaEye size={9} /> Import Agent Key
                  </button>
                ) : (
                  <div className="space-y-1">
                    <div className="flex gap-1">
                      <input
                        type="password"
                        placeholder="Paste agent private key (0x...)"
                        value={importKeyInput}
                        onChange={e => { setImportKeyInput(e.target.value); setImportKeyError(''); }}
                        className="flex-1 font-mono text-xs bg-slate-900 border border-slate-600 rounded px-2 py-1 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-yellow-500"
                        onKeyDown={e => e.key === 'Enter' && handleImportAgentKey()}
                      />
                      <button
                        onClick={handleImportAgentKey}
                        className="px-2 py-0.5 bg-yellow-600 hover:bg-yellow-500 text-white rounded text-xs transition-colors"
                      >Save</button>
                      <button
                        onClick={() => { setShowImportKey(false); setImportKeyError(''); setImportKeyInput(''); }}
                        className="px-2 py-0.5 bg-slate-600 hover:bg-slate-500 text-white rounded text-xs transition-colors"
                      >✕</button>
                    </div>
                    {importKeyError && <p className="text-red-400 text-xs flex items-center gap-1"><FaTimesCircle size={9}/> {importKeyError}</p>}
                    {importKeySuccess && <p className="text-emerald-400 text-xs flex items-center gap-1"><FaCheckCircle size={9}/> Key saved — reloading panel...</p>}
                  </div>
                )}
              </div>
            )}
            {agentPayload?.agent_private_key && (
              <div className="flex items-baseline gap-1.5 leading-tight">
                <span className="text-slate-500 shrink-0">Agent Key:</span>
                <span className="font-mono text-yellow-400 truncate flex-1">
                  {showPrivateKey ? agentPayload.agent_private_key : '••••••••••••••••••••••••••••••••'}
                </span>
                <button
                  onClick={() => setShowPrivateKey(v => !v)}
                  className="shrink-0 flex items-center gap-1 px-1.5 py-0.5 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                  title={showPrivateKey ? 'Hide private key' : 'Show private key'}
                >
                  {showPrivateKey ? <FaEyeSlash size={9} /> : <FaEye size={9} />}
                  {showPrivateKey ? 'Hide' : 'Show'}
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(agentPayload.agent_private_key);
                    setCopiedAgentKey(true);
                    setTimeout(() => setCopiedAgentKey(false), 2000);
                  }}
                  className="shrink-0 flex items-center gap-1 px-1.5 py-0.5 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                  title="Copy agent private key to clipboard"
                >
                  <FaCopy size={9} />
                  {copiedAgentKey ? 'Copied!' : 'Copy'}
                </button>
                <button
                  onClick={() => {
                    removeAgentPrivateKey(portfolioId);
                    bumpAgentKeyVersion();
                    setShowPrivateKey(false);
                    setShowDebug(false);
                    setTimeout(() => setShowDebug(true), 50);
                  }}
                  className="shrink-0 flex items-center gap-1 px-1.5 py-0.5 bg-red-700 hover:bg-red-600 text-white rounded transition-colors"
                  title="Delete agent key from local storage"
                >
                  <FaTrash size={9} />
                  Delete
                </button>
              </div>
            )}
          </div>

          {/* Action bar */}
          <div className="flex items-center gap-2 px-3 py-1.5 border-b border-slate-700 flex-wrap">
            {/* Agent approval status */}
            <div className="flex items-center gap-1.5">
              {checkingApproval ? (
                <span className="flex items-center gap-1 text-yellow-400">
                  <div className="w-2.5 h-2.5 border border-yellow-400 border-t-transparent rounded-full animate-spin" />
                  Checking...
                </span>
              ) : realAgentApproved === true ? (
                <span className="flex items-center gap-1 text-emerald-400"><FaCheckCircle size={10} /> Agent Approved</span>
              ) : realAgentApproved === false ? (
                <span className="flex items-center gap-1 text-red-400"><FaTimesCircle size={10} /> Agent Not Approved</span>
              ) : (
                <span className="flex items-center gap-1 text-slate-400"><FaQuestionCircle size={10} /> Agent Approval Unknown</span>
              )}
            </div>

            <div className="h-3 w-px bg-slate-600" />

            {/* Builder fee approval status */}
            <div className="flex items-center gap-1.5">
              {checkingBuilderFee ? (
                <span className="flex items-center gap-1 text-yellow-400">
                  <div className="w-2.5 h-2.5 border border-yellow-400 border-t-transparent rounded-full animate-spin" />
                  Checking...
                </span>
              ) : builderFeeApproved ? (
                <span className="flex items-center gap-1 text-emerald-400"><FaCheckCircle size={10} /> Builder Fee Approved</span>
              ) : (
                <span className="flex items-center gap-1 text-red-400"><FaTimesCircle size={10} /> Builder Fee Not Approved</span>
              )}
              <button
                onClick={handleApproveBuilderFee}
                disabled={builderFeeApproving || !signer}
                className="flex items-center gap-1 px-2 py-0.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-900 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded transition-colors"
              >
                {builderFeeApproving
                  ? <><div className="w-2.5 h-2.5 border border-white border-t-transparent rounded-full animate-spin" />Approving...</>
                  : 'Approve Builder Fee'}
              </button>
            </div>

            <div className="h-3 w-px bg-slate-600" />

            {/* Single manual refresh */}
            <button
              onClick={handleRefresh}
              disabled={checkingApproval || checkingBuilderFee}
              title="Refresh agent and builder fee status"
              className="flex items-center gap-1 px-2 py-0.5 bg-slate-600 hover:bg-slate-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded transition-colors"
            >
              <FaSync size={9} className={checkingApproval || checkingBuilderFee ? 'animate-spin' : ''} />
              Refresh
            </button>

            <div className="h-3 w-px bg-slate-600" />

            {/* Dev tools */}
            <button
              onClick={() => {
                console.log('=== Manual Agent Payload Test ===');
                console.log('Agent payload result:', agentPayload);
                console.log('Agent approved result:', realAgentApproved);
                console.log('Is testnet:', isTestnetNetwork());
                console.log('Network info:', network);
                alert(`Network: ${network ? `${network.name} (${network.chainId})` : 'Not detected'}\nIs Web3 Testnet: ${isTestnetNetwork()}\nIs HL Testnet: ${portfolio?.is_testnet}\nAgent Payload: ${JSON.stringify(agentPayload, null, 2)}\nAgent Approved: ${realAgentApproved}`);
              }}
              className="px-2 py-0.5 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
            >
              Test Payload
            </button>
            <button
              onClick={async () => {
                if (!agentPayload?.agent_address) { alert('No agent address available'); return; }
                try {
                  const response = await fetch(`${stableConfig.apiBaseUrl}/debug-agent/${agentPayload.agent_address}?is_testnet=${agentPayload.is_testnet}`, {
                    method: 'POST',
                    headers: { ...(authHeaders || {}), 'Content-Type': 'application/json' },
                    body: JSON.stringify(agentPayload)
                  });
                  const result = await response.json();
                  console.log('Agent debug result:', result);
                  alert(`Agent Debug Result:\n${JSON.stringify({ status: result.status, agent_address: result.agent_address, master_address: result.master_address, is_testnet: result.is_testnet, exists_on_hyperliquid: result.exists_on_hyperliquid, user_state: result.user_state }, null, 2)}`);
                } catch (error) {
                  console.error('Failed to debug agent:', error);
                  alert('Failed to debug agent: ' + error.message);
                }
              }}
              className="px-2 py-0.5 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
            >
              Debug on HL
            </button>
          </div>

          {/* Inline feedback messages */}
          {(builderFeeStatus || realAgentApproved === false) && (
            <div className="px-3 py-1.5 space-y-1">
              {builderFeeStatus === 'success' && (
                <p className="flex items-center gap-1 text-emerald-400"><FaCheckCircle size={10} /> {builderFeeMessage}</p>
              )}
              {builderFeeStatus === 'error' && (
                <p className="flex items-center gap-1 text-red-400"><FaTimesCircle size={10} /> {builderFeeMessage}</p>
              )}
              {realAgentApproved === false && agentPayload?.agent_address && (
                <div className="flex items-center gap-2 pt-0.5">
                  <span className="text-yellow-300 font-medium">Manual approval needed →</span>
                  <code className="flex-1 font-mono text-emerald-400 bg-slate-800 rounded px-1.5 py-0.5 truncate">{agentPayload.agent_address}</code>
                  <button onClick={handleCopyAddress} className="flex items-center gap-1 px-1.5 py-0.5 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors whitespace-nowrap">
                    <FaCopy size={9} />{copiedAddress ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Compact inline label: value row
function DRow({ label, value, mono, valueClass = 'text-slate-300' }) {
  return (
    <div className="flex items-baseline gap-1.5 leading-tight">
      <span className="text-slate-500 shrink-0">{label}:</span>
      <span className={`truncate ${mono ? 'font-mono' : ''} ${valueClass}`}>{value}</span>
    </div>
  );
}
